import React, { Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  IconCalendar,
  IconGauge,
  IconMap,
  IconClipboardText,
  IconChartBar,
  IconRadar,
  IconBuilding,
  IconQuestionMark,
  IconBuildingEstate,
  IconBook,
  IconRotate360,
  IconNotebook,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
} from '@tabler/icons-react';
import { LinksGroup } from './LinksGroup';
import { UserButton } from '../user/UserButton';
import { Logo } from './Logo';
import { Group, ActionIcon, Button, Tooltip } from '@mantine/core';
import { ThemeToggle } from './ThemeToggle';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useComputedColorScheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useUserStore } from '../../store/userStore';
import { useUiStore } from '../../store/uiStore';
import { usePendingReviewCount } from '../../hooks/useApi';
import { useScoringAccess } from '../../hooks/useQuarterlyScoreApi';
import { hasGrantOnOthers, useMyPermissions, usePermission } from '../../hooks/usePermission';
import { useDevice } from '../../hooks/useDevice';
import { COMING_SOON } from '../../config/comingSoon';
import { useHasFeature } from '../../store/featureFlagsStore';
import { useCompanyModules } from '../../hooks/useCompanyModules';

const HelpDrawer = React.lazy(() =>
  import('../onboarding/HelpDrawer').then((m) => ({ default: m.HelpDrawer })),
);

interface NavigationItem {
  label: string;
  path?: string;
  /**
   * Pathname prefixes that should NOT make this item active even though they
   * start with `path`. Used to hand off admin subtrees to a more-specific
   * link inside the "Управление" group (e.g. /performance-review/admin/*
   * should highlight Управление → Performance Review, not the top-level
   * Performance Review item).
   */
  excludePaths?: string[];
  icon?: React.ComponentType<{ size?: number; color?: string }>;
  links?: {
    label: string;
    link: string;
    badge?: number;
    comingSoon?: boolean;
    /** false — скрыть: нет права с сервера. */
    isVisible?: boolean;
  }[];
  badge?: number;
  comingSoon?: boolean;
  /** Пре-релиз: пункт доступен всем, но помечен подписью «beta». */
  beta?: boolean;
  /** false — скрыть: нет права с сервера. */
  isVisible?: boolean;
}

export const Navigation: React.FC = () => {
  const location = useLocation();
  const colorScheme = useComputedColorScheme('light');
  const isDark = colorScheme === 'dark';
  const user = useUserStore((s) => s.user);
  const { t } = useTranslation();
  const { data: pendingCount } = usePendingReviewCount();
  const canManageReviews = usePermission('competencies.review');

  // Матрица и итоги квартальной оценки — по правам с сервера, не по флагам admin/scoreObserver:
  // админ существующей компании итогов не видит, владелец без флага — видит.
  const canManageScoring = usePermission('quarterly.scores.manage');
  const canManage360Templates = usePermission('review360.manage');
  const canSeeScoreResults = usePermission('quarterly.scores.results');
  // «Управление» — по правам с сервера: владелец без флага admin видит всё, «Настройщик» —
  // структуру и карты, но не людей и не настройки компании.
  const canManagePeople = usePermission('people.manage');
  const canManageStructure = usePermission('company.structure');
  const canManageMaps = usePermission('maps.manage');
  const canManageSettings = usePermission('company.settings');
  const canManageRounds = usePermission('reviews.manage');
  // «Роли и права» (бета): роли — company.roles; у кого только журнал — сразу на него.
  const hasRolesScreen = useHasFeature('rolesScreen');
  const canManageRoles = usePermission('company.roles');
  const canReadAllGoals = usePermission('goals.read');
  const canSeeAnalyticsTeaser = usePermission('reviews.analytics');
  const canReadAudit = usePermission('company.audit');
  const { data: perms } = useMyPermissions();
  // Права на месячные и квартальные разные (охват считает сервер). Ссылку показываем по
  // любому из двух — страница сама решит, какую вкладку открыть.
  const canManageEssays =
    hasGrantOnOthers(perms, 'essays.read') || hasGrantOnOthers(perms, 'quarterly.read');
  // Аналитика ревью (бета) — по праву reviews.analytics с сервера: на всю компанию или на своих
  // людей (руководитель, руководитель руководителей). Флаги и состав команд тут ни при чём.
  const hasAnalytics =
    useHasFeature('reviewAnalytics') && hasGrantOnOthers(perms, 'reviews.analytics');
  const hasTeamRoles = useHasFeature('teamRolesDictionary');
  const deviceType = useDevice();
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  // Icon-only rail only applies on desktop; mobile is always the full drawer.
  const isCompact = collapsed && deviceType !== 'mobile';
  const [helpOpened, { open: openHelp, close: closeHelp }] = useDisclosure(false);
  // Разделы, не подключённые компании, в меню не показываем (`Tenant.modules`).
  const { hasModule } = useCompanyModules();
  const { data: scoringAccess } = useScoringAccess(hasModule('quarterly'));

  const navigationItems: NavigationItem[] = [
    { label: t('navigation.dashboard'), path: '/dashboard', icon: IconGauge },
    // Календарь открыт всем, но ещё помечен «beta».
    {
      label: t('navigation.calendar'),
      path: '/calendar',
      icon: IconCalendar,
      beta: true,
      isVisible: hasModule('calendar'),
    },
    {
      label: t('navigation.competencyMap'),
      icon: IconMap,
      isVisible: hasModule('competencyMap'),
      links: [
        { label: t('navigation.grades'), link: '/grades' },
        // Без карты или грейда ссылка вела бы на /competency-maps/undefined/undefined.
        ...(user?.competencyMapId && user?.grade?._id
          ? [
              {
                label: t('navigation.myGrade'),
                link: `/competency-maps/${user.competencyMapId}/${user.grade._id}`,
              },
            ]
          : []),
        {
          label: t('navigation.competencyMaps'),
          link: '/competency-maps',
          isVisible: canManageMaps,
        },
        { label: t('competencies.title'), link: '/competencies', isVisible: canManageMaps },
        // Подать заявку на компетенцию может каждый — пункт у всех.
        { label: t('reviewRequests.title'), link: '/review-requests', badge: pendingCount?.count },
        // Все заявки и метрики — по праву competencies.review (владелец без флага admin тоже).
        ...(canManageReviews
          ? [{ label: t('competencyReviews.title'), link: '/competency-reviews' }]
          : []),
      ],
    },
    {
      label: t('navigation.performanceReview'),
      path: '/performance-review',
      isVisible: hasModule('performanceReview'),
      // Admin subtree belongs to the Management group entry below.
      excludePaths: ['/performance-review/admin'],
      icon: IconClipboardText,
    },
    ...(hasAnalytics && hasModule('performanceReview')
      ? [{ label: t('navigation.analytics'), path: '/analytics', icon: IconChartBar, beta: true }]
      : []),
    // Технорадар — про компетенции: без карт компетенций пункта нет.
    {
      label: t('navigation.techRadar'),
      path: '/tech-radar',
      icon: IconRadar,
      comingSoon: true,
      isVisible: hasModule('competencyMap'),
    },
    {
      label: t('navigation.learning'),
      path: '/learning',
      icon: IconBook,
      comingSoon: COMING_SOON.learning,
      isVisible: hasModule('learning'),
    },
    {
      label: t('navigation.essays'),
      icon: IconNotebook,
      beta: true,
      // Группа «Эссе» держит и квартальные: она видна, если подключён хоть один из двух.
      isVisible: hasModule('essays') || hasModule('quarterly'),
      links: [
        { label: t('essays.myEssays'), link: '/essays' },
        ...(canManageEssays ? [{ label: t('essays.manageTitle'), link: '/essays/manage' }] : []),
        // Оценивание — только тем, кого назначали хоть в одном квартале: у
        // остальных пункт ведёт на пустой список и только мешает.
        ...(scoringAccess?.hasAssignments && hasModule('quarterly')
          ? [{ label: t('quarterlyScoring.title'), link: '/quarterly-scoring' }]
          : []),
        ...(canManageScoring && hasModule('quarterly')
          ? [{ label: t('quarterlyScoring.matrix.title'), link: '/quarterly-scoring/matrix' }]
          : []),
        ...(canSeeScoreResults && hasModule('quarterly')
          ? [{ label: t('quarterlyScoring.results.title'), link: '/quarterly-scoring/results' }]
          : []),
      ],
    },
    {
      label: t('review360.title'),
      icon: IconRotate360,
      comingSoon: COMING_SOON.review360,
      isVisible: hasModule('review360'),
      links: [
        { label: t('review360.cycles'), link: '/review-360' },
        ...(canManage360Templates
          ? [{ label: t('review360.templates'), link: '/review-360/templates' }]
          : []),
      ],
    },
    {
      isVisible: canManageSettings,
      label: t('navigation.organizationSettings'),
      path: '/settings/organization',
      icon: IconBuildingEstate,
    },
    {
      // Группа видна, если в ней осталась хоть одна ссылка (фильтр ниже).
      label: t('navigation.management'),
      icon: IconBuilding,
      links: [
        { label: t('navigation.userList'), link: '/users', isVisible: canManagePeople },
        { label: t('navigation.teams'), link: '/teams', isVisible: canManageStructure },
        {
          label: t('navigation.teamRoles'),
          link: '/teams/roles',
          isVisible: canManageStructure && hasTeamRoles,
        },
        { label: t('navigation.professions'), link: '/professions', isVisible: canManageStructure },
        { label: t('navigation.departments'), link: '/departments', isVisible: canManageStructure },
        {
          label: t('navigation.roles'),
          link: canManageRoles ? '/roles' : '/roles/audit',
          isVisible: hasRolesScreen && (canManageRoles || canReadAudit),
        },
        {
          label: t('goals.allGoals'),
          link: '/all-goals',
          isVisible: canReadAllGoals && hasModule('goals'),
        },
        ...(hasAnalytics || !hasModule('performanceReview')
          ? []
          : [
              {
                label: t('navigation.analytics'),
                link: '/analytics',
                comingSoon: true,
                isVisible: canSeeAnalyticsTeaser,
              },
            ]),
        ...(canManageRounds && hasModule('performanceReview')
          ? [{ label: t('navigation.performanceReview'), link: '/performance-review/admin' }]
          : []),
      ],
    },
  ];

  // Пункты и ссылки без права (isVisible: false) прячем
  const filteredNavigationItems = navigationItems
    .filter((item) => item.isVisible !== false)
    .map((item) => {
      // Фильтруем вложенные ссылки
      if (item.links) {
        const filteredLinks = item.links.filter((link) => link.isVisible !== false);

        // Если после фильтрации остались ссылки или элемент не имеет ссылок, возвращаем элемент
        if (filteredLinks.length > 0 || !item.links) {
          return { ...item, links: filteredLinks };
        }
        // Если все ссылки отфильтрованы, скрываем весь элемент
        return null;
      }
      return item;
    })
    .filter((item) => item !== null) as NavigationItem[];

  return (
    <>
      <Group
        wrap="nowrap"
        p="md"
        justify={isCompact ? 'center' : 'flex-start'}
        style={{ borderBottom: '1px solid var(--app-shell-border-color)' }}
        visibleFrom="sm"
      >
        <Logo iconOnly={isCompact} />
      </Group>
      <Group
        py="md"
        px={isCompact ? 'xs' : deviceType === 'mobile' ? '0' : 'md'}
        gap={0}
        mb="auto"
        className="nav-links-scroll"
        style={{
          overflowY: 'auto',
          maxHeight: deviceType === 'mobile' ? 'calc(100vh - 120px)' : 'calc(100vh - 80px)',
          scrollbarWidth: 'thin',
          scrollbarColor: isDark
            ? 'var(--mantine-color-dark-4) transparent'
            : 'var(--mantine-color-gray-4) transparent',
        }}
      >
        {filteredNavigationItems.map((item) => (
          <LinksGroup
            key={item.label}
            icon={item.icon}
            label={item.label}
            path={item.path}
            links={item.links}
            badge={item.badge}
            excludePaths={item.excludePaths}
            comingSoon={item.comingSoon}
            beta={item.beta}
            collapsed={isCompact}
            initiallyOpened={
              item.links?.some(
                (link) =>
                  location.pathname === link.link || location.pathname.startsWith(link.link + '/'),
              ) || false
            }
          />
        ))}
      </Group>
      <Group
        visibleFrom="sm"
        p="md"
        style={{
          backgroundColor: isDark ? 'var(--mantine-color-dark-6)' : 'var(--mantine-color-gray-0)',
          color: 'var(--mantine-color-teal-7)',
        }}
      >
        <Group
          px={isCompact ? 0 : 'md'}
          gap="sm"
          justify="center"
          style={isCompact ? { flexDirection: 'column', width: '100%' } : undefined}
        >
          <ThemeToggle />
          <ActionIcon
            variant="outline"
            color="teal"
            size="sm"
            onClick={openHelp}
            title={t('help.title')}
          >
            <IconQuestionMark size={12} />
          </ActionIcon>
          <LanguageSwitcher compact={isCompact} />
          <Tooltip
            label={isCompact ? t('navigation.expandSidebar') : t('navigation.collapseSidebar')}
            position="right"
            withArrow
          >
            <ActionIcon
              variant="outline"
              color="teal"
              size="sm"
              onClick={toggleSidebar}
              aria-label={
                isCompact ? t('navigation.expandSidebar') : t('navigation.collapseSidebar')
              }
            >
              {isCompact ? (
                <IconLayoutSidebarLeftExpand size={12} />
              ) : (
                <IconLayoutSidebarLeftCollapse size={12} />
              )}
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      {/* На телефоне нижняя панель со справкой скрыта — даём вход отдельной строкой. */}
      <Group hiddenFrom="sm" px="md" pb="xs">
        <Button
          variant="subtle"
          color="teal"
          size="sm"
          leftSection={<IconQuestionMark size={16} />}
          onClick={openHelp}
        >
          {t('help.title')}
        </Button>
      </Group>
      <Suspense fallback={null}>
        <HelpDrawer opened={helpOpened} onClose={closeHelp} />
      </Suspense>
      <Group
        p={deviceType === 'mobile' ? '0' : 'md'}
        style={{ borderTop: '1px solid var(--app-shell-border-color)', width: '100%' }}
      >
        <UserButton collapsed={isCompact} />
      </Group>
    </>
  );
};
